import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import { useLocationSuggestions } from "@/utils/hooks/useLocationQuery";
import { LocationSuggestion } from "@/types/location";
export const useLocationAutocompleteWithQuery = (initialLocation?: string) => {
  const [inputValue, setInputValue] = useState<string>(initialLocation || "");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [isLocationSelected, setIsLocationSelected] = useState<boolean>(!!initialLocation);
  const [hasShownSuggestions, setHasShownSuggestions] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [previouslySelectedValues, setPreviouslySelectedValues] = useState<Set<string>>(new Set(initialLocation ? [initialLocation] : []));
  const [shouldShowErrorOnSubmit, setShouldShowErrorOnSubmit] = useState<boolean>(false);
  const [justSelectedSuggestion, setJustSelectedSuggestion] = useState<boolean>(false);
  // Add a flag to track if this is an initial location from localStorage
  const [isInitialLocation, setIsInitialLocation] = useState<boolean>(!!initialLocation);
  const [selectedLatLng, setSelectedLatLng] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  // Add ref to track if we're in the middle of a form submission
  const isSubmittingRef = useRef<boolean>(false);

  // Add refs to track timeouts for cleanup
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      if (selectTimeoutRef.current) clearTimeout(selectTimeoutRef.current);
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
    };
  }, []);

  // Use React Query for suggestions
  const {
    data: suggestions = [],
    isLoading: isLoadingSuggestions,
    error: suggestionsError
  } = useLocationSuggestions(debouncedQuery, true);

  // Create a debounced function using Lodash
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetQuery = useCallback(debounce((query: string) => {
    setDebouncedQuery(query);
  }, 500), []);
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // If user starts typing, it's no longer an initial location
    // BUT only if the value is different from the initial location
    if (isInitialLocation && value !== initialLocation) {
      setIsInitialLocation(false);
    }

    // Check if current value exactly matches a previously selected value
    if (previouslySelectedValues.has(value)) {
      setIsLocationSelected(true);
      setShouldShowErrorOnSubmit(false);
    } else {
      // Reset selection state when user types something different
      setIsLocationSelected(false);
    }

    // Use debounced function to set query
    if (value.length >= 3) {
      debouncedSetQuery(value);
      setHasShownSuggestions(true);
    } else {
      debouncedSetQuery.cancel(); // Cancel any pending debounced calls
      setDebouncedQuery("");
      setHasShownSuggestions(false);
    }
  }, [debouncedSetQuery, previouslySelectedValues, isInitialLocation, initialLocation]);

  // Add useEffect to handle when inputValue is set externally (like from useEffect in parent component)
  useEffect(() => {
    // If inputValue matches the initial location, ensure isInitialLocation is true
    if (inputValue === initialLocation && initialLocation) {
      setIsInitialLocation(true);
      setIsLocationSelected(true);
      setShouldShowErrorOnSubmit(false);
      // Add to previously selected values if not already there
      setPreviouslySelectedValues(prev => {
        if (!prev.has(inputValue)) {
          return new Set([...prev, inputValue]);
        }
        return prev;
      });
    }
  }, [inputValue, initialLocation]);
  const handleInputFocus = useCallback(() => {
    setIsFocused(true);

    // If input has 3+ characters and is focused, trigger suggestions immediately
    if (inputValue.length >= 3) {
      setDebouncedQuery(inputValue);
      setHasShownSuggestions(true);
    }
  }, [inputValue]);
  const handleInputBlur = useCallback(() => {
    // Add a longer delay to prevent blur from interfering with click events
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);

      // Skip validation if a suggestion was just selected
      if (justSelectedSuggestion) {
        setJustSelectedSuggestion(false);
        // Close suggestions dropdown
        setDebouncedQuery("");
        setHasShownSuggestions(false);
        return;
      }

      // Check if user blurred with 3+ characters and suggestions were shown
      if (inputValue.length >= 3 && hasShownSuggestions) {
        // Check if current value exactly matches a previously selected value
        // OR if it's the initial location from localStorage
        if (previouslySelectedValues.has(inputValue) || isInitialLocation) {
          // Exact match or initial location - no error
          setIsLocationSelected(true);
          setShouldShowErrorOnSubmit(false);
        } else {
          // No exact match - mark for error on submit
          setIsLocationSelected(false);
          setShouldShowErrorOnSubmit(true);
        }
      }

      // Close suggestions dropdown
      setDebouncedQuery("");
      setHasShownSuggestions(false);
    }, 200); // Increased delay to 200ms to better handle click events
  }, [inputValue, hasShownSuggestions, justSelectedSuggestion, previouslySelectedValues, isInitialLocation]);
  const handleSelectSuggestion = useCallback((suggestion: LocationSuggestion, setValue: (field: string, value: string) => void) => {
    const selectedValue = suggestion.description;
    setInputValue(selectedValue);
    setValue("location", selectedValue);
    setIsLocationSelected(true);
    setHasShownSuggestions(false);
    setShouldShowErrorOnSubmit(false);
    setJustSelectedSuggestion(true);
    setIsInitialLocation(false);
    setPreviouslySelectedValues(prev => new Set([...prev, selectedValue]));
    setDebouncedQuery("");

    // Google Maps JS API logic
    if (typeof window !== "undefined" && window.google && window.google.maps) {
      const service = new window.google.maps.places.PlacesService(document.createElement("div"));
      service.getDetails({
        placeId: suggestion.place_id,
        fields: ["geometry"]
      }, (result, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && result && result.geometry && result.geometry.location) {
          const lat = result.geometry.location.lat();
          const lng = result.geometry.location.lng();
          setSelectedLatLng({
            lat,
            lng
          });
          localStorage.setItem("business_location", JSON.stringify({
            lat,
            lng
          }));
        } else {
          setSelectedLatLng(null);
        }
      });
    }
    selectTimeoutRef.current = setTimeout(() => {
      setJustSelectedSuggestion(false);
    }, 300);
  }, []);

  // Function to handle form submission attempt
  const handleFormSubmitAttempt = useCallback(() => {
    isSubmittingRef.current = true;

    // Force close dropdown and validate immediately
    setDebouncedQuery("");
    setHasShownSuggestions(false);
    setIsFocused(false);

    // If user has 3+ characters, check if they selected from dropdown
    if (inputValue.length >= 3) {
      // Check if current value exactly matches a previously selected value
      // OR if it's the initial location from localStorage
      if (previouslySelectedValues.has(inputValue) || isInitialLocation) {
        // Exact match or initial location - no error
        setIsLocationSelected(true);
        setShouldShowErrorOnSubmit(false);
      } else {
        // No exact match - mark for error on submit
        setIsLocationSelected(false);
        setShouldShowErrorOnSubmit(true);
      }
    }

    // Reset submission flag after a short delay
    submitTimeoutRef.current = setTimeout(() => {
      isSubmittingRef.current = false;
    }, 100);
  }, [inputValue, previouslySelectedValues, isInitialLocation, hasShownSuggestions, isLocationSelected]);

  // Function to check if location is valid for form submission (synchronous)
  const checkLocationValidForSubmit = useCallback((): boolean => {
    if (!inputValue || inputValue.trim().length === 0) {
      return false;
    }
    if (inputValue.length < 3) {
      return false;
    }

    // If user has 3+ characters, they must have selected from dropdown (regardless of hasShownSuggestions state)
    // OR if it's the initial location from localStorage
    if (inputValue.length >= 3) {
      // Check if current value exactly matches a previously selected value
      // OR if it's the initial location from localStorage
      if (previouslySelectedValues.has(inputValue) || isInitialLocation) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }, [inputValue, previouslySelectedValues, isInitialLocation]);

  // Custom validation function for location (internal use only)
  const validateLocation = useCallback((value: string): string | true => {
    if (!value || value.trim().length === 0) {
      return "Location is required";
    }
    if (value.length < 3) {
      return "Location must be at least 3 characters long";
    }

    // If user has 3+ characters and hasn't selected from suggestions, show error
    // UNLESS it's the initial location from localStorage
    if (value.length >= 3 && !isLocationSelected && !isInitialLocation) {
      return "Please select a location from the suggestions";
    }
    return true;
  }, [isLocationSelected, isInitialLocation]);

  // Check if location is valid internally (for form submission)
  const isLocationValid = useCallback((): boolean => {
    // Use the same logic as validateLocation
    if (!inputValue || inputValue.trim().length === 0) {
      return false;
    }
    if (inputValue.length < 3) {
      return false;
    }

    // If user has 3+ characters and hasn't selected from suggestions, show error
    // UNLESS it's the initial location from localStorage
    if (inputValue.length >= 3 && !isLocationSelected && !isInitialLocation) {
      return false;
    }
    const result = true;
    return result;
  }, [inputValue, isLocationSelected, isInitialLocation]);

  // Get error message for form submission
  const getLocationErrorMessage = useCallback((): string | null => {
    if (!inputValue || inputValue.trim().length === 0) {
      return "Location is required";
    }
    if (inputValue.length < 3) {
      return "Location must be at least 3 characters long";
    }

    // If user has 3+ characters, they must select from dropdown
    // UNLESS it's the initial location from localStorage
    if (inputValue.length >= 3 && !previouslySelectedValues.has(inputValue) && !isInitialLocation) {
      return "Please select a location from the suggestions";
    }
    return null;
  }, [inputValue, previouslySelectedValues, isInitialLocation]);
  useEffect(() => {
    return () => {
      // Cancel any pending debounced calls on cleanup
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);
  const value = useMemo(() => {
    return {
      inputValue,
      suggestions,
      handleInputChange,
      handleInputFocus,
      handleInputBlur,
      handleSelectSuggestion,
      handleFormSubmitAttempt,
      checkLocationValidForSubmit,
      setInputValue,
      isLoadingSuggestions,
      suggestionsError,
      validateLocation,
      isLocationValid,
      getLocationErrorMessage,
      isLocationSelected,
      hasShownSuggestions,
      isFocused,
      shouldShowErrorOnSubmit,
      justSelectedSuggestion,
      isInitialLocation,
      selectedLatLng
    };
  }, [getLocationErrorMessage, handleInputBlur, handleInputChange, handleInputFocus, handleSelectSuggestion, handleFormSubmitAttempt, checkLocationValidForSubmit, hasShownSuggestions, inputValue, isFocused, isLoadingSuggestions, isLocationSelected, isLocationValid, justSelectedSuggestion, shouldShowErrorOnSubmit, suggestions, suggestionsError, validateLocation, isInitialLocation, selectedLatLng]);
  return value;
};